import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';

export function MetricCard({ label, value, subtitle, trend, loading }) {
  if (loading) {
    return (
      <div className="panel p-5" data-testid="metric-card-skeleton">
        <Skeleton className="h-3 w-20 mb-3 bg-gray-100" />
        <Skeleton className="h-8 w-32 mb-2 bg-gray-100" />
        <Skeleton className="h-3 w-24 bg-gray-100" />
      </div>
    );
  }

  return (
    <div className="panel p-5 panel-hover" data-testid={`metric-card-${label?.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="overline">{label}</span>
      <div className="mt-2 font-mono text-2xl lg:text-3xl font-semibold text-[#1B2A4A] tracking-tight tabular-nums" data-testid="metric-value">
        {value}
      </div>
      {(subtitle || trend) && (
        <div className="mt-1.5 flex items-center gap-2">
          {subtitle && <span className="text-xs text-[#6B7280]">{subtitle}</span>}
          {trend && (
            <span className={`text-xs font-mono font-medium ${trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function ChartCard({ title, children, loading }) {
  if (loading) {
    return (
      <div className="panel p-5" data-testid="chart-card-skeleton">
        <Skeleton className="h-3 w-28 mb-4 bg-gray-100" />
        <Skeleton className="h-48 w-full bg-gray-100" />
      </div>
    );
  }

  return (
    <div className="panel p-5" data-testid={`chart-card-${title?.toLowerCase().replace(/\s+/g, '-')}`}>
      {title && <span className="overline mb-3 block">{title}</span>}
      {children}
    </div>
  );
}

export function ActivityItem({ type, description, amount, date, status }) {
  const statusColor = status === 'processed' || status === 'filled'
    ? 'text-emerald-600'
    : status === 'pending'
    ? 'text-amber-600'
    : 'text-gray-500';

  return (
    <div className="flex items-center justify-between py-3 border-b border-[#F0F1F3] last:border-0" data-testid="activity-item">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#1B2A4A] font-medium truncate">{description}</p>
        <p className="text-xs text-[#9CA3AF] font-mono mt-0.5">{date}</p>
      </div>
      <div className="text-right ml-4 shrink-0">
        {amount && <p className="text-sm font-mono font-medium text-[#1B2A4A]">{amount}</p>}
        {status && <p className={`text-[10px] font-mono uppercase tracking-wider ${statusColor}`}>{status}</p>}
      </div>
    </div>
  );
}

export function QuickAction({ icon: Icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="flex items-center gap-3 px-4 py-3 bg-white border border-[#E2E5E9] rounded-md text-sm text-[#374151] hover:border-[#1B2A4A] hover:text-[#1B2A4A] hover:shadow-sm transition-all w-full"
    >
      {Icon && <Icon size={16} strokeWidth={1.5} className="text-[#6B7280]" />}
      <span>{label}</span>
    </button>
  );
}

export function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-state">
      {Icon && <Icon size={40} strokeWidth={1} className="text-[#D1D5DB] mb-3" />}
      <p className="text-sm font-medium text-[#6B7280]">{title}</p>
      {description && <p className="text-xs text-[#9CA3AF] mt-1">{description}</p>}
    </div>
  );
}

export function SkeletonDashboard() {
  return (
    <div className="space-y-4" data-testid="skeleton-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <MetricCard key={i} loading />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard loading />
        <ChartCard loading />
      </div>
    </div>
  );
}
